<template>

  <ui-card name="overview.time_visualization" :is-loading="isLoading" :error="error" padded>
    <ui-day-filter slot="controls" />

    <heatmap-chart v-if="data && data.length > 0" :data="data" :tooltip-content="tooltipContent" @interaction="onInteraction" />

    <div v-else-if="isLoading" class="columns">
      <div class="column is-left">
        <div class="blokk-y-axis">
          <div class="blokk shimmer blokk-tick is-small">----</div>
          <div class="blokk shimmer blokk-tick is-small">----</div>
          <div class="blokk shimmer blokk-tick is-small">------</div>
          <div class="blokk shimmer blokk-tick is-small">-----</div>
        </div>
      </div>
      <div class="column is-right">
        <div class="blokk-grid is-clearfix">
          <div class="blokk-grid-item is-pulled-left" v-for="(item, index) in new Array(96)" :key="index"></div>
        </div>
        <div class="blokk-chart-divider"></div>
        <div class="blokk shimmer blokk-x-axis">
          <span>----</span><span>-</span><span>-</span><span>-</span><span>----</span><span>-</span><span>-</span><span>-</span><span>----</span>
        </div>
      </div>
    </div>

  </ui-card>

</template>

<script>
import { has, zipObject, filter, find, groupBy, keys, reduce } from 'lodash';
import { map, sortBy, sortedUniqBy, flow } from 'lodash/fp';
import HeatmapChart from 'views/charts/HeatmapChart';
import { card } from 'views/mixins';

export default {
  extends: card('filter-dates', 'filter-days'),

  components: {
    HeatmapChart,
  },

  data() {
    return {
      dataByTime: [],
      tooltipContent: null,
    };
  },

  methods: {
    makeQuery(params) {
      return {
        app_id: params.appId,
        transformations: [],
        sets: [
          {
            store: 'event_time',
            measures: [
              {
                measure: 'duration_hour_pct',
                agg: 'none',
                custom_id: 'value',
              },
            ],
            dimensions: [
              {
                dim: 'hour',
                custom_id: 'hour',
              },
              {
                dim: 'event_significance',
                custom_id: 'type',
              },
            ],
            filters: [
              {
                scope: 'req',
                type: 'range',
                dim: 'isotime',
                begin: params.from,
                end: params.to,
              },
              {
                scope: 'req',
                type: 'values',
                dim: 'day_of_week',
                values: params.domain,
              },
            ],
          },
        ],
      };
    },

    parseData(set) {
      let fields = set.fields;
      let rows = set.rows;

      fields = map(field => field.custom_id)(fields);

      rows = flow(
        map(x => zipObject(fields, x)),
        sortBy([this.sortByType, 'hour'])
      )(rows);

      return rows;
    },

    sortByType(o) {
      const preferredOrder = ['home', 'work', 'transport', 'other'];

      return preferredOrder.indexOf(o.type);
    },

    onInteraction(newValue) {
      if (!newValue) {
        return;
      }

      const duration = newValue[1] - newValue[0];

      if (duration === 1) {
        const activeItem = this.dataByTime[newValue[0]];

        if (!activeItem) {
          return;
        }

        const content = activeItem.map(item => {
          return {
            type: item.type,
            value: {
              path: 'overview.time_visualization.tooltip_users_amount',
              args: { amount: (item.value * 100).toFixed(2) },
            },
          };
        });

        this.tooltipContent = content;

        return;
      }

      const activeTime = keys(this.dataByTime).filter(
        o => o >= newValue[0] && o < newValue[1]
      );

      const types = flow(
        sortedUniqBy('type'),
        map(item => item.type)
      )(this.data);

      const content = map(type => {
        const total = reduce(
          activeTime,
          (result, value) => {
            const item = find(this.dataByTime[value], o => o.type === type);

            if (item && item.value) {
              return result + item.value;
            }

            return result;
          },
          0
        );

        return {
          type: type,
          value: {
            path: 'overview.time_visualization.tooltip_users_amount',
            args: { amount: ((total / duration) * 100).toFixed(2) },
          },
        };
      })(types);

      this.tooltipContent = content;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.content {
  position: relative;
  height: 100%;
}

.heatmap-chart {
  display: flex;
  height: 100%;
}

.columns {
  height: 100%;
}

.column {
  &.is-left {
    flex: 0 0 4.5rem;
    padding-right: 0;
  }

  &.is-right {
    margin-right: 0.1875rem;
  }
}

.blokk-grid {
  margin-top: 0.75rem;
}

.blokk-grid-item {
  display: inline-flex;
  width: percentage(1/24);
  height: 10px;
  background: $grey-lighter;
  border-right: 0.5px solid $white;
  margin-bottom: 2.25rem;
}

.blokk-chart-divider {
  background-color: $grey-darker;
  border: none;
  display: block;
  height: 1px;
  opacity: 0.2;
  margin-top: -1px;
}

.blokk-x-axis {
  display: flex;
  justify-content: space-between;
  margin-top: 1.25rem;
  font-size: $size-small;
}

.blokk-y-axis {
  margin-top: 0.625rem;
  font-size: $size-small;

  .blokk-tick {
    line-height: 1;
    margin-bottom: 2.125rem;
  }
}
</style>